// Destination hand-off for the magic-link sign-in flow.
//
// WHY THIS EXISTS
// Phase 2 of email notifications mails "your report is ready" links that point
// at `/research/view?id=…`. An unauthenticated click bounces to the login page
// with `?next=<path>` (`loginWithReturn`, web/src/lib/routes.ts). Password and
// SSO sign-in both honour that param; magic-link did not — the verify page
// hard-coded `router.replace('/')`. Since this product has no password reset,
// magic-link is the LIKELY path for someone arriving from an email, so the
// promise "click to open your report" was false roughly half the time.
//
// WHY localStorage AND NOT sessionStorage
// This is the crux and it is easy to get wrong. `sessionStorage` is scoped to
// ONE top-level browsing context. The mailed link is opened in a NEW TAB (from
// webmail, or handed to the browser by a native mail client), which starts with
// an EMPTY session storage — same device, same browser, still empty. Using
// sessionStorage here would be a silent no-op for the exact flow this code
// exists to fix. `localStorage` is shared across tabs of the same origin, which
// is precisely the hand-off we need. Opening the link on a *different device*
// still degrades to the dashboard root — today's behaviour, and acceptable.
//
// WHY THE `lvdr_session_` PREFIX
// The stored path can carry a research id, i.e. a hint about the user's own
// content. That makes it session-tied, not machine-tied, so it takes the
// prefix that `hardLogout` auto-wipes (see web/src/lib/auth/hardLogout.ts).
// The wipe cannot break the feature: the expired-session bounce runs
// hardLogout BEFORE landing on the login page, so the entry is always written
// after the wipe. A concurrent logout in another tab merely degrades the next
// mailed link to `/`, which is the safe direction.
//
// SECURITY
// The value feeds a redirect and localStorage is attacker-writable from any XSS
// on this origin, so it is re-validated on READ with the same rule the login
// page applies to `?next=` — never trusted because we wrote it. It is also
// TTL-bounded and consumed (cleared) on read, so a stale destination cannot
// hijack a later, unrelated sign-in.

const STORAGE_KEY = 'lvdr_session_magic_link_next';

/** How long a pending destination stays valid. Long enough to walk to an inbox,
 *  short enough that a forgotten entry cannot capture a later sign-in. */
export const MAGIC_LINK_NEXT_TTL_MS = 30 * 60 * 1000;

interface StoredNext {
  path: string;
  exp: number;
}

/**
 * Same-origin path check used by the login page for `?next=`:
 * a single leading slash, never `//` (protocol-relative → cross-origin).
 *
 * Backslashes are rejected on top of that rule: URL parsers normalise `\` to
 * `/`, so `/\evil.com` resolves cross-origin in Chrome even though it passes
 * the naive check. Strictly stronger — no legitimate in-app path contains one.
 */
export function isSafeNextPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\\')
  );
}

/** Drop any pending destination. Safe to call when storage is unavailable. */
export function clearMagicLinkNext(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage disabled (Safari private mode, hardened profiles) — the feature
    // degrades to the dashboard root, which is the pre-existing behaviour.
  }
}

/**
 * Remember where to land after a magic link is redeemed.
 *
 * An absent or unsafe path CLEARS instead of storing, so requesting a link
 * without a `next` can never inherit a destination from an earlier request.
 */
export function storeMagicLinkNext(path: string | null | undefined): void {
  if (!isSafeNextPath(path)) {
    clearMagicLinkNext();
    return;
  }
  const entry: StoredNext = { path, exp: Date.now() + MAGIC_LINK_NEXT_TTL_MS };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota/disabled — see clearMagicLinkNext.
  }
}

/**
 * Read and consume the pending destination.
 *
 * ALWAYS clears, whatever it found: a corrupt, expired, hostile or successfully
 * used entry must not survive to influence a later sign-in. Returns `undefined`
 * unless a live, well-formed, same-origin path was present — callers fall back
 * to `/`.
 */
export function consumeMagicLinkNext(): string | undefined {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
  clearMagicLinkNext();
  if (!raw) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined;

  const { path, exp } = parsed as Partial<StoredNext>;
  if (typeof exp !== 'number' || !Number.isFinite(exp) || Date.now() > exp) return undefined;
  // Re-validate: storage is attacker-writable, so the write-side check is not
  // a substitute for checking here.
  if (!isSafeNextPath(path)) return undefined;
  return path;
}
